import React from 'react';
import { Link } from 'react-router-dom';
import { useCollections } from '../hooks/useCollections';
import { useFeed } from '../hooks/useListings';
import CollectionCard from '../components/CollectionCard';
import ListingCard from '../components/ListingCard';
import { EmptyState, LoadingState, StateForError } from '../components/PageStates';
import { Button } from '@/components/ui/button';

/** Home: what's new from the people I'm connected with, and a few collections. */
export default function MarketplaceHome() {
  const feed = useFeed();
  const collections = useCollections();

  return (
    <div className="space-y-12">
      <div className="rounded-3xl bg-brand-forest p-8 text-brand-forest-foreground">
        <h1 className="mb-4 text-4xl font-bold tracking-tight">Welcome to the Marketplace</h1>
        <p className="mb-6 text-xl">What the people you trade with are passing along.</p>
        <div className="flex flex-wrap gap-3">
          <Button asChild variant="secondary" size="lg" className="rounded-full">
            <Link to="/listings/create">Post a listing</Link>
          </Button>
          <Button asChild variant="secondary" size="lg" className="rounded-full">
            <Link to="/collections/create">New collection</Link>
          </Button>
        </div>
      </div>

      <section aria-labelledby="home-new">
        <h2 id="home-new" className="mb-6 text-2xl font-bold text-foreground">
          New for you
        </h2>
        {feed.status === 'loading' ? (
          <LoadingState label="Loading new listings" />
        ) : feed.status === 'error' ? (
          <StateForError error={feed.error} onRetry={feed.reload} />
        ) : feed.data.length === 0 ? (
          <EmptyState
            title="No listings yet"
            description="Listings from people you're connected with show up here."
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {feed.data.map((listing) => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="home-collections">
        <div className="mb-6 flex items-center justify-between">
          <h2 id="home-collections" className="text-2xl font-bold text-foreground">
            Collections
          </h2>
          <Link to="/my-collections" className="font-medium text-primary hover:text-primary/80">
            View all →
          </Link>
        </div>
        {collections.status === 'loading' ? (
          <LoadingState label="Loading collections" />
        ) : collections.status === 'error' ? (
          <StateForError error={collections.error} onRetry={collections.reload} />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {collections.data.slice(0, 6).map((collection) => (
              <CollectionCard key={collection.id} collection={collection} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
